// 📊 Números de Kontrol (solo la admin): cuánto cuesta mantener Kontrol, cuánto deja
// cada plan y la proyección de 12 meses. Lógica pura (sin React) para poder probarla
// y reusarla en el Artifact "Números de Kontrol". Todo en lempiras salvo los costos
// unitarios de proveedores, que vienen en dólares.

export const PLANES_NK = [
  { k: 'emp', nombre: '🌱 Emprende' },
  { k: 'cre', nombre: '⭐ Crece' },
  { k: 'pro', nombre: '🚀 Pro' },
];

// Qué consume cada función de IA (US$ por uso), medido en oct-2026.
export const USOS_IA = [
  { k: 'fotos', nombre: 'Fotos con IA', proveedor: 'Kie.ai · nano-banana-edit (4 créditos = US$0.02)' },
  { k: 'descripciones', nombre: 'Descripciones con IA', proveedor: 'Claude Opus 4.8 (1 oración)' },
  { k: 'ideas', nombre: 'Ideas de contenido', proveedor: 'Claude Haiku 5.5' },
  { k: 'materiales', nombre: 'Publicaciones listas (materiales)', proveedor: 'Claude Haiku 5.5, lista corta' },
  { k: 'premium', nombre: 'Versión premium', proveedor: 'Claude Opus 5.5' },
  { k: 'planMes', nombre: 'Plan del mes con IA (Mis metas)', proveedor: 'Claude Opus 5.5, esfuerzo medio' },
  { k: 'planDia', nombre: '¿Qué hago hoy? (Mis metas)', proveedor: 'Claude Opus 5.5, esfuerzo bajo' },
];

export const SUPUESTOS_INICIALES = {
  tasa: 26.8,
  precios: { emp: 299, cre: 599, pro: 1099 },
  fundador: { emp: 249, cre: 499, pro: 899 },
  nFund: 25,
  mezcla: { emp: 50, cre: 35, pro: 15 },
  isv: true,
  pasarela: { parte: 0, pct: 4.5, fijo: 0.3 }, // parte = % de clientes que pagan con tarjeta (hoy: transferencia)
  unit: { fotos: 0.02, descripciones: 0.01, ideas: 0.005, materiales: 0.005, premium: 0.16, planMes: 0.1, planDia: 0.05 },
  fallos: 6, // % de fotos que se repiten por fallo
  // Uso típico al mes por cliente de cada plan.
  uso: {
    emp: { fotos: 8, descripciones: 10, ideas: 10, materiales: 10, premium: 1, planMes: 1, planDia: 8 },
    cre: { fotos: 35, descripciones: 30, ideas: 25, materiales: 30, premium: 5, planMes: 2, planDia: 15 },
    pro: { fotos: 120, descripciones: 60, ideas: 60, materiales: 100, premium: 15, planMes: 3, planDia: 20 },
  },
  // Lo máximo que deja usar Kontrol por plan (sql/130, 21-oct-2026).
  tope: {
    emp: { fotos: 15, descripciones: 60, ideas: 60, materiales: 30, premium: 3, planMes: 2, planDia: 30 },
    cre: { fotos: 60, descripciones: 200, ideas: 150, materiales: 80, premium: 10, planMes: 5, planDia: 60 },
    pro: { fotos: 200, descripciones: 300, ideas: 400, materiales: 200, premium: 25, planMes: 10, planDia: 60 },
  },
  // Servidor: lo que ocupa y descarga cada cliente (GB). Supabase Pro trae 100 GB y 250 GB.
  infra: {
    storage: { emp: 0.3, cre: 0.8, pro: 2 },
    egress: { emp: 1.5, cre: 4, pro: 8 },
    incluidoStorage: 100, incluidoEgress: 250, usdStorage: 0.021, usdEgress: 0.09,
    subirServidorDesde: 300, usdServidorGrande: 15,
  },
  fijos: { supabase: 670, vercel: 536, dominio: 30, whatsapp: 300, claude: 536, contador: 1000, otros: 100, sueldo: 0 },
  cortesias: 4,
  ads: 2500, cpl: 80, conv: 25, churn: 8, hoy: 0,
  escenarios: [50, 150, 250],
};

export const NOMBRES_FIJOS = {
  supabase: 'Supabase Pro (base de datos y fotos) · US$25',
  vercel: 'Vercel Pro (la app en internet) · US$20',
  dominio: 'Dominio y correo',
  whatsapp: 'WhatsApp de ventas (chip y saldo)',
  claude: 'Claude (para seguir construyendo Kontrol)',
  contador: 'Contador',
  otros: 'Otras herramientas',
  sueldo: 'Tu sueldo como dueña',
};

const n = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
const suma = (o) => Object.values(o || {}).reduce((a, b) => a + n(b), 0);

// Costo de IA de un cliente al mes (lempiras) con un perfil de uso.
export function costoIA(perfil, s) {
  return USOS_IA.reduce((t, u) => {
    const veces = n(perfil?.[u.k]) * (u.k === 'fotos' ? 1 + n(s.fallos) / 100 : 1);
    return t + veces * n(s.unit[u.k]) * n(s.tasa);
  }, 0);
}

// Servidor por cliente si ya pasaste lo incluido en Supabase Pro (lempiras).
export function costoServidor(k, s) {
  const i = s.infra;
  return (n(i.storage[k]) * n(i.usdStorage) + n(i.egress[k]) * n(i.usdEgress)) * n(s.tasa);
}

// Desglose por cliente de cada plan: precio, impuesto, pasarela, IA y lo que queda.
export function porPlan(s, { fundador = false, uso = 'uso' } = {}) {
  const parte = n(s.pasarela.parte) / 100;
  return PLANES_NK.map((p) => {
    const precio = n((fundador ? s.fundador : s.precios)[p.k]);
    const isv = s.isv ? precio - precio / 1.15 : 0;
    const pasarela = parte * (precio * n(s.pasarela.pct) / 100 + n(s.pasarela.fijo) * n(s.tasa));
    const ia = costoIA(s[uso][p.k], s);
    const servidor = costoServidor(p.k, s);
    const neto = precio - isv - pasarela - ia - servidor;
    return { ...p, precio, isv, pasarela, ia, servidor, neto, margen: precio > 0 ? neto / precio : 0 };
  });
}

export function pesos(s) {
  const t = suma(s.mezcla) || 1;
  return Object.fromEntries(PLANES_NK.map((p) => [p.k, n(s.mezcla[p.k]) / t]));
}

export const totalFijos = (s) => suma(s.fijos);
export const cac = (s) => Math.max(1, n(s.cpl)) / Math.max(0.01, n(s.conv) / 100);

// Un mes con cierta cantidad de clientes por plan. Los primeros nFund pagan precio fundador.
export function mes(conteos, s) {
  const normal = porPlan(s);
  const fund = porPlan(s, { fundador: true });
  const total = suma(conteos);
  const parteF = total > 0 ? Math.min(1, n(s.nFund) / total) : 0;
  let ingreso = 0; let isv = 0; let pasarela = 0; let ia = 0;
  PLANES_NK.forEach((p, i) => {
    const c = n(conteos[p.k]); const f = c * parteF; const r = c - f;
    ingreso += r * normal[i].precio + f * fund[i].precio;
    isv += r * normal[i].isv + f * fund[i].isv;
    pasarela += r * normal[i].pasarela + f * fund[i].pasarela;
    ia += c * normal[i].ia;
  });
  // Cortesías: no pagan, pero usan IA y servidor como un Pro típico.
  const cort = n(s.cortesias);
  ia += cort * costoIA(s.uso.pro, s);
  const i = s.infra;
  const gb = (campo) => PLANES_NK.reduce((t, p) => t + n(conteos[p.k]) * n(i[campo][p.k]), 0) + cort * n(i[campo].pro);
  const servidor = (Math.max(0, gb('storage') - n(i.incluidoStorage)) * n(i.usdStorage)
    + Math.max(0, gb('egress') - n(i.incluidoEgress)) * n(i.usdEgress)
    + (total + cort >= n(i.subirServidorDesde) ? n(i.usdServidorGrande) : 0)) * n(s.tasa);
  const fijos = totalFijos(s);
  const reponer = total * (n(s.churn) / 100) * cac(s);
  const queda = ingreso - isv - pasarela - ia - servidor - fijos - reponer;
  return { total, fundadores: total * parteF, ingreso, isv, pasarela, ia, servidor, fijos, reponer, queda };
}

export function repartir(total, s) {
  const w = pesos(s);
  return Object.fromEntries(PLANES_NK.map((p) => [p.k, total * w[p.k]]));
}

// 12 meses desde noviembre: entran clientes nuevos por anuncios y se va el % de churn.
export const MESES_NK = ['Nov', 'Dic', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct'];
export function proyeccion(s) {
  const nuevos = n(s.ads) / cac(s);
  const churn = n(s.churn) / 100;
  let c = n(s.hoy); let acum = 0; let peor = 0; let equilibrio = null;
  const filas = MESES_NK.map((m, idx) => {
    const r = mes(repartir(c, s), s);
    // La publicidad del mes reemplaza la "reposición": se paga completa.
    const gan = r.queda + r.reponer - n(s.ads);
    acum += gan;
    if (acum < peor) peor = acum;
    if (equilibrio === null && gan >= 0) equilibrio = idx;
    const fila = { mes: m, clientes: c, ingreso: r.ingreso, costos: r.ingreso - r.queda - r.reponer + n(s.ads), gan, acum };
    c = c * (1 - churn) + nuevos;
    return fila;
  });
  return { filas, nuevos, techo: churn > 0 ? nuevos / churn : 0, colchon: -peor, equilibrio };
}

export function resumenMezcla(s) {
  const w = pesos(s);
  const pp = porPlan(s);
  const precio = pp.reduce((t, p) => t + p.precio * w[p.k], 0);
  const neto = pp.reduce((t, p) => t + p.neto * w[p.k], 0);
  const fijos = totalFijos(s);
  return { precio, neto, margen: precio > 0 ? neto / precio : 0, equilibrio: neto > 0 ? Math.ceil(fijos / neto) : null };
}

// Cuánto deja un cliente en toda su vida vs lo que costó conseguirlo.
export function publicidad(s) {
  const vida = 1 / Math.max(0.01, n(s.churn) / 100);
  const costo = cac(s);
  return porPlan(s).map((p) => ({ ...p, vida, deja: p.neto * vida, maximo: (p.neto * vida) / 3, recuperaMeses: p.neto > 0 ? costo / p.neto : null, veces: costo > 0 ? (p.neto * vida) / costo : 0 }));
}

// Mezcla los supuestos guardados con los iniciales (por si se agregan campos nuevos).
export function combinar(base, guardado) {
  if (!guardado || typeof guardado !== 'object') return base;
  const out = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(base).forEach((k) => {
    const b = base[k]; const g = guardado[k];
    if (g === undefined || g === null) return;
    if (b && typeof b === 'object' && !Array.isArray(b)) out[k] = combinar(b, g);
    else if (Array.isArray(b)) out[k] = Array.isArray(g) ? g.map((x, i) => (Number.isFinite(Number(x)) ? Number(x) : b[i])) : b;
    else out[k] = typeof b === 'number' ? (Number.isFinite(Number(g)) ? Number(g) : b) : g;
  });
  return out;
}

// Lo que ofrece Kontrol y qué tipo de costo genera.
export const FUNCIONES = [
  { grupo: 'Vender', items: ['Vender (punto de venta, también sin internet)', 'Ventas de hoy y corte del día', 'Cierre de mes', 'Comprobantes y factura CAI', 'Por cobrar (fiado)'], costo: 'servidor' },
  { grupo: 'Productos', items: ['Inventario con códigos y etiquetas', 'Stock bajo y reponer', 'Ofertas en lote', 'Mostrar u ocultar del catálogo', 'Venta por libra (distribuidoras)'], costo: 'servidor' },
  { grupo: 'Tienda en línea', items: ['Catálogo boutique con bolsa de compras', 'Pedidos con etapas, apartado y enlace para la clienta', 'Avisos al celular de cada pedido', 'Catálogo para Meta (feed) y conteo de visitas'], costo: 'servidor' },
  { grupo: 'Servicios', items: ['Catálogo de servicios', 'Cotizaciones con enlace y PDF', 'Agenda de citas', 'Órdenes de servicio con fotos'], costo: 'servidor' },
  { grupo: 'Dinero', items: ['Balance, cuentas y gastos', 'Reportes'], costo: 'nada' },
  { grupo: 'Clientes', items: ['CRM con segmentos', 'WhatsApp y correo en lote', 'Campaña de recompra'], costo: 'nada' },
  { grupo: 'Con IA', items: ['Fotos profesionales con IA', 'Descripciones con IA', 'Agente de contenido (ideas, publicaciones, premium)', 'Mis metas: plan del mes y ¿qué hago hoy?'], costo: 'ia' },
  { grupo: 'Redes y anuncios', items: ['Carruseles, historias y video automático', 'Publicar y programar en Instagram y Facebook', 'Resultados de publicaciones', 'Panel de anuncios de Meta con ventas reales'], costo: 'nada' },
  { grupo: 'Equipo y cuenta', items: ['Mi equipo (vendedoras con permisos)', 'Mis metas con recordatorios', 'App instalable (PWA)', 'Varios países y monedas', 'Respaldo de fotos'], costo: 'nada' },
];
